import { FileUpload } from "@admin-ui/components/application/file-upload/file-upload-base";
import { RadioGroupIconSimple } from "@admin-ui/components/base/radio-groups/radio-group-icon-simple";
import { Avatar } from "@admin-ui/components/primitives/base/avatar/avatar";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { CloseButton } from "@admin-ui/components/primitives/base/buttons/close-button";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import { RadioButton, RadioGroup } from "@admin-ui/components/primitives/base/radio-buttons/radio-buttons";
import { FeaturedIcon } from "@admin-ui/components/primitives/foundations/featured-icon";
import { ArrowLeft, Globe01, GraduationHat02, UserPlus01, Users01 } from "@untitledui/icons";
import { type FormEvent, useState } from "react";
import { Heading as AriaHeading } from "react-aria-components";

export type AccountTypeLabel = "Family" | "Community" | "Individual";
export type Visibility = "public" | "private" | "hidden";

type Step = "type" | "details";

const TYPE_OPTIONS = [
  {
    value: "Community",
    title: "Community",
    secondaryTitle: "",
    description: "A worldschooling community or hub",
    icon: Globe01,
  },
  {
    value: "Family",
    title: "Family",
    secondaryTitle: "",
    description: "A family travelling or worldschooling together",
    icon: Users01,
  },
  {
    value: "Individual",
    title: "Individual",
    secondaryTitle: "",
    description: "An independent individual or tutor",
    icon: GraduationHat02,
  },
];

export interface NewViewProps {
  accountType: AccountTypeLabel;
  onAccountTypeChange: (type: AccountTypeLabel) => void;
  name: string;
  onNameChange: (value: string) => void;
  contactName: string;
  onContactNameChange: (value: string) => void;
  contactEmail: string;
  onContactEmailChange: (value: string) => void;
  visibility: Visibility;
  onVisibilityChange: (value: Visibility) => void;
  avatarPreview: string | null;
  onAvatarDrop: (files: FileList) => void;
  onAvatarRemove: () => void;
  coverPreview: string | null;
  onCoverDrop: (files: FileList) => void;
  onCoverRemove: () => void;
  canSubmit: boolean;
  processing: boolean;
  onSubmit: (e: FormEvent) => void;
  onClose: () => void;
}

export function NewView({
  accountType,
  onAccountTypeChange,
  name,
  onNameChange,
  contactName,
  onContactNameChange,
  contactEmail,
  onContactEmailChange,
  visibility,
  onVisibilityChange,
  avatarPreview,
  onAvatarDrop,
  onAvatarRemove,
  coverPreview,
  onCoverDrop,
  onCoverRemove,
  canSubmit,
  processing,
  onSubmit,
  onClose,
}: NewViewProps) {
  const [step, setStep] = useState<Step>("type");

  const needsProfileName = accountType !== "Individual";
  const nameLabel = accountType === "Family" ? "Family name" : "Community name";
  const namePlaceholder = accountType === "Family" ? "The Smiths" : "Bali Worldschoolers";

  return (
    <div className="pointer-events-auto relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-120">
      <CloseButton
        onPress={onClose}
        theme="light"
        size="sm"
        className="absolute top-3 right-3 z-10 sm:top-4 sm:right-4"
      />

      {step === "type" && (
        <>
          <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
            <FeaturedIcon color="gray" size="md" theme="modern" icon={UserPlus01} />
            <div className="flex flex-col gap-1">
              <AriaHeading slot="title" className="font-semibold text-lg text-primary">
                New account
              </AriaHeading>
              <p className="text-sm text-tertiary">What type of account are you creating?</p>
            </div>
          </div>

          <div className="px-4 pt-5 sm:px-6">
            <RadioGroupIconSimple
              aria-label="Account type"
              value={accountType}
              onChange={(value) => onAccountTypeChange(value as AccountTypeLabel)}
              items={TYPE_OPTIONS}
            />
          </div>

          <div className="flex flex-1 flex-col-reverse gap-3 p-4 pt-6 sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
            <Button size="md" color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button size="md" color="primary" onClick={() => setStep("details")}>
              Continue
            </Button>
          </div>
        </>
      )}

      {step === "details" && (
        <form onSubmit={onSubmit}>
          <div className="flex flex-col gap-1 px-4 pt-5 sm:px-6 sm:pt-6">
            <AriaHeading slot="title" className="font-semibold text-lg text-primary">
              {accountType} details
            </AriaHeading>
            <p className="text-sm text-tertiary">Fill in what you have — the invitee can update later.</p>
          </div>

          <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6">
            {needsProfileName && (
              <Input
                label={nameLabel}
                placeholder={namePlaceholder}
                value={name}
                onChange={onNameChange}
                isRequired
                size="md"
              />
            )}

            <Input
              label="Contact name"
              placeholder="Jane Doe"
              value={contactName}
              onChange={onContactNameChange}
              isRequired
              size="md"
              hint="The person who will claim this account."
            />

            <Input
              label="Contact email"
              placeholder="jane@example.com"
              type="email"
              value={contactEmail}
              onChange={onContactEmailChange}
              isRequired
              size="md"
              hint="The invitation can only be claimed from this address."
            />

            <div className="flex flex-col gap-1.5">
              <span className="font-medium text-secondary text-sm">Profile photo</span>
              {avatarPreview ? (
                <div className="flex items-center gap-4">
                  <Avatar size="xl" src={avatarPreview} />
                  <Button size="sm" color="secondary" type="button" onClick={onAvatarRemove}>
                    Remove
                  </Button>
                </div>
              ) : (
                <FileUpload.Root>
                  <FileUpload.DropZone
                    accept="image/jpeg,image/png,image/webp"
                    maxSize={10 * 1024 * 1024}
                    onDropFiles={onAvatarDrop}
                  />
                </FileUpload.Root>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="font-medium text-secondary text-sm">Cover photo</span>
              {coverPreview ? (
                <div className="relative">
                  <img src={coverPreview} alt="Cover preview" className="aspect-[3/1] w-full rounded-lg object-cover" />
                  <Button
                    size="sm"
                    color="secondary"
                    type="button"
                    className="absolute right-2 bottom-2"
                    onClick={onCoverRemove}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <FileUpload.Root>
                  <FileUpload.DropZone
                    accept="image/jpeg,image/png,image/webp"
                    maxSize={10 * 1024 * 1024}
                    onDropFiles={onCoverDrop}
                  />
                </FileUpload.Root>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="font-medium text-secondary text-sm">Visibility</span>
              <RadioGroup
                aria-label="Visibility"
                value={visibility}
                onChange={(next) => onVisibilityChange(next as Visibility)}
                className="gap-2"
              >
                <RadioButton value="public" label="Public" hint="Show at /@handle with a claim banner." />
                <RadioButton
                  value="private"
                  label="Private"
                  hint="Profile visible but content hidden from non-friends."
                />
                <RadioButton value="hidden" label="Hidden" hint="Only visible to admin." />
              </RadioGroup>
            </div>
          </div>

          <div className="flex flex-1 flex-col-reverse gap-3 p-4 pt-6 sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
            <Button size="md" color="secondary" iconLeading={ArrowLeft} onClick={() => setStep("type")} type="button">
              Back
            </Button>
            <Button size="md" color="primary" isDisabled={!canSubmit || processing} type="submit">
              Create account
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
